import { ToastIntent } from '@/common/components/base';
import { config } from '@/common/config';
import { BaseAppModel } from '@/common/models/AppModel';
import { LobbyActionType } from '@/common/models/LobbySessionActions';
import {
  LobbyActionTransfer,
  NetworkDataType,
  SessionInfoTransferH2C,
} from '@/common/models/NetworkDataTransfers';
import { ToastManager } from '@/common/models/ToastManager';
import { generateName } from '@/common/utils/code-gen';
import { makeSimpleAutoObservable } from '@/common/utils/mobx/mobx';
import { HosterNetworkHandler } from '@/hoster/models/HosterNetworkHandler';
import { GameInfo } from '@/types';
import { HosterGlobalSettings, MaturityLevel } from '@toilet-paper-games/core';
import { autorun, reaction, runInAction, toJS } from 'mobx';
import { createContext, useContext } from 'react';
import { HosterUIModel } from './HosterUIModel';
import { HosterUserModel } from './HosterUserModel';

export enum ScreenState {
  LOBBY = 'lobby',
  GAME = 'game',
}

export enum SessionStorageKeys {
  UUID = 'tpg-hoster-uuid',
}

export enum LocalStorageKeys {
  DEV_MODE = 'tpg-hoster-dev-mode',
  DEV_GAME_URL = 'tpg-hoster-dev-game-url',
  FORCE_SHOW_EXIT_BUTTON = 'tpg-hoster-force-show-exit-button',
  SHOW_HIDDEN_GAMES = 'tpg-hoster-show-hidden-games',
}

interface LobbyActionHandler extends LobbyActionTransfer {
  userId: string;
  type: NetworkDataType.LOBBY_ACTION;
}

export class HosterAppModel extends BaseAppModel {
  screenState: ScreenState = ScreenState.LOBBY;
  networkHandler: HosterNetworkHandler;
  uuid: string;
  toastManager: ToastManager = new ToastManager();
  isPlayingGame: boolean = false;
  uiModel = new HosterUIModel();
  globalSettings: HosterGlobalSettings | null = null;
  gameSuggestions = new Map<string, string>();
  isFullscreen = false;
  lobbyGame: GameInfo | null = null;

  constructor() {
    super();
    makeSimpleAutoObservable(this, {}, { autoBind: true });

    this.uuid = this.getOrCreateUuid();
    this.networkHandler = new HosterNetworkHandler(this.uuid);

    this.loadLocalStorageSettings();
    this.setupAutoruns();
    this.setupNetworkHandlerListeners();
    this.setupListeners();
    this.setupReactions();
    this.setupStorage();
    this.init();
  }

  private getOrCreateUuid(): string {
    const storedUuid = sessionStorage.getItem(SessionStorageKeys.UUID);
    if (storedUuid) return storedUuid;

    const newUuid = generateName();
    sessionStorage.setItem(SessionStorageKeys.UUID, newUuid);
    return newUuid;
  }

  private loadLocalStorageSettings() {
    this.devMode = localStorage.getItem(LocalStorageKeys.DEV_MODE) === 'true';
    this.devGameUrl = localStorage.getItem(LocalStorageKeys.DEV_GAME_URL);
    this.forceShowExitButton =
      localStorage.getItem(LocalStorageKeys.FORCE_SHOW_EXIT_BUTTON) === 'true';
    this.showHiddenGames =
      localStorage.getItem(LocalStorageKeys.SHOW_HIDDEN_GAMES) === 'true';
  }

  private setupAutoruns() {
    autorun(() => {
      this.gamesList.dev = this.devMode ? this.devGameUrl : null;
    });

    autorun(() => {
      this.networkHandler.userManager.connectedUsers.forEach((user) => {
        user.connection?.send({
          type: NetworkDataType.SESSION_INFO_H2C,
          data: toJS({ ...this.syncedData, user: user.dtoForControllerUserModel }),
        });
      });
    });

    this.setupDevModeHandler();
  }

  private setupStorage() {
    this.fetchGlobalSettings().then((settings) => {
      this.globalSettings = settings;
    });
  }

  private setupDevModeHandler = () => {
    autorun(() => {
      if (this.devMode) {
        localStorage.setItem(LocalStorageKeys.DEV_MODE, 'true');
      } else {
        localStorage.removeItem(LocalStorageKeys.DEV_MODE);
      }
    });

    autorun(async () => {
      if (this.devGameUrl) {
        localStorage.setItem(LocalStorageKeys.DEV_GAME_URL, this.devGameUrl);
      } else {
        localStorage.removeItem(LocalStorageKeys.DEV_GAME_URL);
      }
    });

    autorun(() => {
      if (this.forceShowExitButton) {
        localStorage.setItem(LocalStorageKeys.FORCE_SHOW_EXIT_BUTTON, 'true');
      } else {
        localStorage.removeItem(LocalStorageKeys.FORCE_SHOW_EXIT_BUTTON);
      }
    });

    autorun(() => {
      if (this.showHiddenGames) {
        localStorage.setItem(LocalStorageKeys.SHOW_HIDDEN_GAMES, 'true');
      } else {
        localStorage.removeItem(LocalStorageKeys.SHOW_HIDDEN_GAMES);
      }
    });
  };

  private get globalSettingsKey(): string {
    return `tpg-hoster-global-settings`;
  }

  async fetchGlobalSettings(): Promise<HosterGlobalSettings> {
    const content = localStorage.getItem(this.globalSettingsKey);
    return content ? JSON.parse(content) : this.getDefaultGlobalSettings();
  }

  private getDefaultGlobalSettings(): HosterGlobalSettings {
    return {
      maturityLevel: MaturityLevel.Mature,
      musicVolume: 0.5,
      narrationVolume: 0.8,
      soundFxVolume: 0.7,
    };
  }

  async updateGlobalSettings<T extends keyof HosterGlobalSettings>(
    key: T,
    value: HosterGlobalSettings[T],
  ) {
    const content = await this.fetchGlobalSettings();
    runInAction(() => {
      content[key] = value;
      localStorage.setItem(this.globalSettingsKey, JSON.stringify(content));
      this.globalSettings = content;
    });
  }

  get syncedData(): Omit<SessionInfoTransferH2C['data'], 'user'> {
    return {
      connectedUsers: toJS(
        this.networkHandler.userManager.connectedUsers.map((user) =>
          toJS(user.dtoForControllerUserModel),
        ),
      ),
      devMode: this.devMode,
      devGameUrl: this.devGameUrl,
      forceShowExitButton: this.forceShowExitButton,
      showHiddenGames: this.showHiddenGames,
      selectedGame: this.selectedGame,
      isPlayingGame: this.isPlayingGame,
      hosterGlobalSettings: toJS(this.globalSettings),
    };
  }

  private setupReactions() {
    this.setupScreenStateReaction();
    this.fullscreenReaction();
    this.lobbyGameReaction();
    this.gamesListReaction();
  }

  lobbyGameReaction() {
    return reaction(
      () => this.games.get(config.lobbyGameId),
      (lobbyGame) => {
        if (!lobbyGame) {
          console.error('Lobby game not found');
          return;
        }
        this.lobbyGame = lobbyGame;
      },
      { fireImmediately: true },
    );
  }

  gamesListReaction() {
    return reaction(
      () => toJS(this.gamesList),
      () => {
        this.loadGames();
      },
      { fireImmediately: true },
    );
  }

  private setupListeners() {
    this.initializeFullscreenListeners();
  }

  private setupScreenStateReaction() {
    reaction(
      () => this.isPlayingGame,
      (isPlayingGame) => {
        this.screenState = isPlayingGame ? ScreenState.GAME : ScreenState.LOBBY;
      },
      { fireImmediately: true },
    );
  }

  private setupNetworkHandlerListeners() {
    this.networkHandler.addDataListener(
      this.handleLobbyAction,
      NetworkDataType.LOBBY_ACTION,
    );
    this.networkHandler.addDataListener(this.handleEndGame, NetworkDataType.END_GAME);
    this.networkHandler.addConnectionListener(this.handleUserConnection);
    this.networkHandler.addDisconnectionListener(this.handleUserDisconnection);
    this.networkHandler.addDataListener(
      this.handleUpdateHosterSettings,
      NetworkDataType.UPDATE_HOSTER_SETTINGS_C2H,
    );
  }

  private handleLobbyAction = (data: LobbyActionHandler) => {
    const action = data.data.action;
    const lobbyActionType = action.type;
    const user = this.networkHandler.userManager.usersMap.get(data.userId);
    if (!user) {
      console.error('User not found', data.userId);
      return;
    }

    switch (lobbyActionType) {
      case LobbyActionType.SELECT_GAME:
        if (!user?.isHost) {
          this.suggestGame(action.data.gameUniqueName, user);
        } else {
          this.trySelectGame(action.data.gameUniqueName);
        }
        break;
      case LobbyActionType.SUGGEST_GAME:
        this.suggestGame(action.data.gameUniqueName, user);
        break;
      case LobbyActionType.START_GAME:
        if (!user?.isHost) break;
        this.startGame();
        break;
      case LobbyActionType.PROMOTE_LEADER:
        this.promoteLeader(data.userId, action.data.uuid);
        break;
      case LobbyActionType.DEMOTE_LEADER:
        this.demoteLeader(data.userId, action.data.uuid);
        break;
      case LobbyActionType.KICK_USER:
        this.handleKickUser(data.userId, action.data.userId);
        break;
      default:
        console.warn(`Unhandled lobby action type: ${lobbyActionType}`);
    }
  };

  private handleKickUser(callerId: string, userId: string) {
    const user = this.networkHandler.userManager.usersMap.get(userId);
    const caller = this.networkHandler.userManager.usersMap.get(callerId);

    if (!user || !caller || !caller.isHost) return;

    user.kick('manual');
    this.toastManager.addToast({
      title: `${user.screenName} was kicked by ${caller.screenName}`,
      duration: 6000,
      intent: ToastIntent.WARNING,
    });
  }

  private handleEndGame = () => {
    this.endGame();
  };

  private handleUserConnection = async (userId: string) => {
    const user = this.networkHandler.userManager.usersMap.get(userId);
    this.networkHandler.userManager.kickedUsers.delete(userId);
    if (!user || !user.connection) {
      console.error('User or connection not found', userId);
      return;
    }

    user.connection.addDataListener(() => {
      user.networkReady = true;
    }, NetworkDataType.FETCH_SESSION_INFO_C2H);

    this.toastManager.addToast({
      title: `${user.screenName ?? 'A new player'} joined`,
      duration: 6000,
      intent: ToastIntent.INFO,
    });
  };

  private handleUserDisconnection = async (userId: string) => {
    const user = this.networkHandler.userManager.usersMap.get(userId);
    if (!user || !user.connection) {
      console.error('User or connection not found', userId);
      return;
    }
    if (this.networkHandler.userManager.kickedUsers.has(userId)) return;
    this.toastManager.addToast({
      title: `${user.screenName} disconnected`,
      duration: 6000,
      intent: ToastIntent.INFO,
    });
  };

  private handleUpdateHosterSettings = ({ data }: any) => {
    this.updateGlobalSettings(data.key, data.value);
  };

  startGame() {
    if (!this.selectedGame) {
      console.error('No game selected');
      return;
    }

    this.networkHandler.broadcast({
      type: NetworkDataType.START_GAME,
      data: { game: this.selectedGame },
    });

    this.isPlayingGame = true;
  }

  promoteLeader(callerUid: string, newHostUid: string) {
    if (!this.isUserHostFromUid(callerUid)) return;

    const newHost = this.networkHandler.userManager.usersMap.get(newHostUid);
    if (!newHost) return;

    newHost.isHost = true;
    this.toastManager.addToast({
      title: `${newHost.screenName} was promoted to host`,
      duration: 6000,
      intent: ToastIntent.INFO,
    });
  }

  demoteLeader(callerUid: string, newHostUid: string) {
    if (!this.isUserHostFromUid(callerUid)) return;

    const newHost = this.networkHandler.userManager.usersMap.get(newHostUid);
    if (!newHost) return;

    newHost.isHost = false;
    this.toastManager.addToast({
      title: `${newHost.screenName} was demoted from host`,
      duration: 6000,
      intent: ToastIntent.INFO,
    });
  }

  isUserHostFromUid(uuid: string): boolean {
    if (uuid === this.uuid) return true;

    const user = this.networkHandler.userManager.usersMap.get(uuid);
    return user?.isHost ?? false;
  }

  trySelectGame(gameUniqueName: string): boolean {
    const game = this.games.get(gameUniqueName ?? '');
    if (!game) {
      this.toastManager.addToast({
        title: 'Game Selection Error',
        description: 'Game not found',
        duration: 3000,
        intent: ToastIntent.ERROR,
      });
      return false;
    }

    this.selectedGame = game;
    return true;
  }

  private suggestGame(gameUniqueName: string, user: HosterUserModel) {
    const game = this.games.get(gameUniqueName ?? '');
    if (!game) {
      return false;
    }
    const title = `${user?.screenName ?? 'Someone'} suggests ${game.displayName}`;
    this.toastManager.addToast({ title });
    this.gameSuggestions.set(user.id, gameUniqueName);
  }

  endGame = () => {
    this.networkHandler.broadcast({
      type: NetworkDataType.END_GAME,
      data: undefined,
    });
    this.isPlayingGame = false;
  };

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
  }

  fullscreenReaction() {
    return reaction(
      () => this.isFullscreen,
      (isFullscreen) => {
        if (isFullscreen) {
          document.documentElement.requestFullscreen().catch(() => {
            console.warn('Error entering fullscreen');
            this.isFullscreen = false;
          });
        } else {
          document.exitFullscreen().catch(() => {
            console.warn('Error exiting fullscreen');
          });
        }
      },
    );
  }

  initializeFullscreenListeners() {
    // Create a new stylesheet for cursor styles
    const cursorStyleSheet = this.createCursorStyleSheet();

    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = document.fullscreenElement !== null;
    });

    let cursorTimeout: number | null = null;

    document.addEventListener('mousemove', () => {
      this.updateCursorStyle(cursorStyleSheet, true); // Remove cursor styling when moving the mouse
      if (cursorTimeout) {
        clearTimeout(cursorTimeout);
      }

      if (this.isFullscreen) {
        cursorTimeout = window.setTimeout(() => {
          this.updateCursorStyle(cursorStyleSheet, false); // Hide the cursor after timeout
        }, 1_500);
      }
    });
  }

  // Helper to create a new stylesheet for cursor styles
  createCursorStyleSheet(): CSSStyleSheet {
    const styleElement = document.createElement('style');
    document.head.appendChild(styleElement);
    return styleElement.sheet as CSSStyleSheet;
  }

  // Helper to update the cursor style in the new stylesheet
  updateCursorStyle(styleSheet: CSSStyleSheet, show: boolean) {
    // Clear existing rules
    while (styleSheet.cssRules.length > 0) {
      styleSheet.deleteRule(0);
    }
    // Add a new rule for the '*' selector
    if (!show) {
      styleSheet.insertRule(`* { cursor: none !important; }`, 0);
    }
  }
}

export const hosterAppModelContext = createContext<HosterAppModel | null>(null);
export function useHosterAppModel() {
  const hosterAppModel = useContext(hosterAppModelContext);
  if (!hosterAppModel) throw new Error('hosterAppModelContext not found');
  return hosterAppModel;
}

export function useHosterNetworkHandler(): HosterNetworkHandler {
  return useHosterAppModel().networkHandler;
}
